// 岗位状态：draft 草稿 / open 招聘中（含「已发布」，两者不做区分）/ paused 暂停招聘 / closed 已关闭
export type PositionStatus = 'draft' | 'open' | 'paused' | 'closed';

export const POSITION_STATUS_LABELS: Record<PositionStatus, string> = {
  draft: '草稿', open: '招聘中', paused: '暂停招聘', closed: '已关闭',
};

export interface Position {
  id: string;
  name: string;
  department: string;
  sub_department?: string;
  description: string;
  status: PositionStatus;
  code?: string;
  org_unit_id?: string | null;
  owner_id?: string | null;
  owner_name?: string | null;
  published_at?: string | null;
  candidate_count?: number;
  // 列表聚合列（GET /api/positions）
  bound_count?: number;   // active 绑定的编制数
  used_count?: number;    // 其中已被占用（OCCUPIED）的编制数
  offer_count?: number;   // 进行中的 Offer 数
  shared_with_me?: boolean; // 我在 user_positions 中但不是 owner
  is_public?: number;
  location?: string;
  salary_range?: string;
  requirements?: string;
  priority?: string;
  background?: string;
  employment_type?: string;          // fulltime / intern / outsource / consultant / temp
  expected_onboard_date?: string;
  headcount_target?: number;         // 招聘意图人数，实际招聘数量由绑定的编制决定
  interviewer_ids?: string;          // 岗位级默认面试官（JSON 数组字符串，存 users.id），供「发起评审」自动带出
  funnel?: Record<string, number>;
  created_at: string;
  updated_at: string;
}

export interface Tag {
  id: string;
  name: string;
  color: string;
}

export interface User {
  id: string;
  username: string;
  display_name: string;
  role: 'admin' | 'hr' | 'interviewer' | 'vip' | 'referrer' | 'it';
  status: 'active' | 'disabled' | 'pending';
  position_ids?: string[];
  permissions?: { modules: string[]; position_scope: 'all' | 'specific' };
  feishu_open_id?: string | null;
  phone?: string;
  created_at?: string;
}

export interface ReviewTask {
  id: string;
  candidate_id: string;
  interviewer_id: string;
  callback_token: string;
  status: 'pending' | 'completed' | 'expired';
  result?: 'passed' | 'rejected' | null;
  interviewer_name?: string;
  candidate_name?: string;
  created_at: string;
  updated_at: string;
}

export interface Evaluation {
  id: string;
  candidate_id: string;
  user_id?: string;
  interview_record_id?: string | null;
  author_name?: string;
  // 代交面评：这条评价实际归属的面试官（user_id 仍为实际操作人）
  on_behalf_of?: string | null;
  on_behalf_of_name?: string | null;
  stage: string;
  rating: number;
  result: 'pending' | 'passed' | 'rejected';
  comment: string;
  is_private?: number;
  created_at: string;
}

export interface ActivityLog {
  id: string;
  candidate_id: string;
  action: string;
  detail: string;
  user_id?: string;
  operator_name?: string;
  created_at: string;
}

export interface Candidate {
  id: string;
  name: string;
  phone: string;
  email: string;
  wechat?: string;
  gender: string;
  age: number;
  photo_url: string;
  latest_company: string;
  latest_title: string;
  work_experiences?: { company: string; title: string }[];
  project_experiences?: string[];
  education: string;
  school: string;
  major: string;
  work_years: number;
  expected_salary: string;
  expected_city: string;
  resume_file: string;
  portfolio_url: string;
  personal_website: string;
  source: string;
  referred_by?: string;
  referrer_name?: string;
  tracking_code?: string;
  position_id: string | null;
  position_name?: string;
  stage: CandidateStage;
  status: 'pending' | 'passed' | 'rejected';
  notes: string;
  tags?: Tag[];
  evaluations?: Evaluation[];
  activity_logs?: ActivityLog[];
  resume_text?: string;
  resume_snapshots?: { id: string; source: string; created_at: string }[];
  jd_match_score?: number;
  jd_match_summary?: string;
  is_favorite?: boolean;
  created_at: string;
  updated_at: string;
}

export type CandidateStage =
  | 'new'
  | 'screening'
  | 'screening_passed'
  | 'screening_rejected'
  | 'first_interview'
  | 'first_interview_rejected'
  | 'second_interview'
  | 'second_interview_rejected'
  | 'final_interview'
  | 'final_interview_rejected'
  | 'offer'
  | 'offer_rejected'
  | 'pending_onboard'
  | 'onboarded';

export const STAGE_LABELS: Record<CandidateStage, string> = {
  new: '待筛选',
  screening: '筛选中',
  screening_passed: '筛选通过',
  screening_rejected: '筛选淘汰',
  first_interview: '一面',
  first_interview_rejected: '一面淘汰',
  second_interview: '二面',
  second_interview_rejected: '二面淘汰',
  final_interview: '终面',
  final_interview_rejected: '终面淘汰',
  offer: 'Offer中',
  offer_rejected: '拒Offer',
  pending_onboard: '待入职',
  onboarded: '已入职',
};

export const PIPELINE_STAGES: CandidateStage[] = [
  'new',
  'screening',
  'screening_passed',
  'screening_rejected',
  'first_interview',
  'first_interview_rejected',
  'second_interview',
  'second_interview_rejected',
  'final_interview',
  'final_interview_rejected',
  'offer',
  'offer_rejected',
  'pending_onboard',
  'onboarded',
];

// 主流程阶段（不包含淘汰阶段），用于"通过"按钮的流转
export const MAIN_FLOW_STAGES: CandidateStage[] = [
  'new',
  'screening',
  'screening_passed',
  'first_interview',
  'second_interview',
  'final_interview',
  'offer',
  'pending_onboard',
  'onboarded',
];

// 用于UI展示的主流程阶段分组（带子分类）
export const DISPLAY_STAGE_GROUPS = [
  { 
    key: 'new', 
    label: 'HR筛选', 
    stages: ['new'],
    subCategories: null
  },
  { 
    key: 'screening', 
    label: '面试官筛选', 
    stages: ['screening', 'screening_passed', 'screening_rejected'],
    subCategories: [
      { key: 'screening', label: '待筛选', stage: 'screening' },
      { key: 'screening_passed', label: '筛选通过', stage: 'screening_passed' },
      { key: 'screening_rejected', label: '筛选淘汰', stage: 'screening_rejected' }
    ]
  },
  { 
    key: 'first_interview', 
    label: '一面', 
    stages: ['first_interview', 'first_interview_rejected'],
    subCategories: [
      { key: 'first_interview', label: '进行中', stage: 'first_interview' },
      { key: 'first_interview_rejected', label: '一面淘汰', stage: 'first_interview_rejected' }
    ]
  },
  { 
    key: 'second_interview', 
    label: '二面', 
    stages: ['second_interview', 'second_interview_rejected'],
    subCategories: [
      { key: 'second_interview', label: '进行中', stage: 'second_interview' },
      { key: 'second_interview_rejected', label: '二面淘汰', stage: 'second_interview_rejected' }
    ]
  },
  { 
    key: 'final_interview', 
    label: '终面', 
    stages: ['final_interview', 'final_interview_rejected'],
    subCategories: [
      { key: 'final_interview', label: '进行中', stage: 'final_interview' },
      { key: 'final_interview_rejected', label: '终面淘汰', stage: 'final_interview_rejected' }
    ]
  },
  { 
    key: 'offer', 
    label: 'Offer中', 
    stages: ['offer', 'offer_rejected'],
    subCategories: [
      { key: 'offer', label: 'Offer中', stage: 'offer' },
      { key: 'offer_rejected', label: '拒Offer', stage: 'offer_rejected' }
    ]
  },
  { 
    key: 'pending_onboard', 
    label: '待入职', 
    stages: ['pending_onboard'],
    subCategories: null
  },
  { 
    key: 'onboarded', 
    label: '已入职', 
    stages: ['onboarded'],
    subCategories: null
  },
];

export const EDUCATION_OPTIONS = [
  '高中', '大专', '本科', '硕士', '博士', '其他',
];

export const SOURCE_OPTIONS = [
  'Boss直聘', '拉勾', '猎聘', '智联招聘', '内推', '猎头', '官网', '其他',
];

// ====== 需求发起 ======
export type NeedStatus = 'debating' | 'discussing' | 'confirmed' | 'cancelled';
export type NeedUrgency = 'low' | 'normal' | 'high' | 'urgent';

export const URGENCY_LABELS: Record<NeedUrgency, string> = {
  low: '不急',
  normal: '正常',
  high: '紧急',
  urgent: '非常紧急',
};

export const NEED_STATUS_LABELS: Record<NeedStatus, string> = {
  debating: 'AI辩论中',
  discussing: '讨论中',
  confirmed: '已确认',
  cancelled: '已取消',
};

export interface HiringNeed {
  id: string;
  department: string;
  business_problem: string;
  headcount: number;
  urgency: NeedUrgency;
  status: NeedStatus;
  created_by: string;
  creator_name?: string;
  confirmed_by?: string;
  confirmed_at?: string;
  position_id?: string;
  need_source?: string;
  jd_title: string;
  jd_responsibilities: string;
  jd_requirements: string;
  first_interviewer_ids?: string[];
  second_interviewer_ids?: string[];
  comment_count?: number;
  member_count?: number;
  created_at: string;
  updated_at: string;
}

export interface NeedMember {
  need_id: string;
  user_id: string;
  role: 'creator' | 'collaborator';
  display_name: string;
  username: string;
  user_role: string;
  added_at: string;
}

export interface NeedDebate {
  id: string;
  need_id: string;
  side: 'pro' | 'con' | 'judge';
  content: string;
  created_at: string;
}

export type OrgUnitType = 'department' | 'team' | 'squad';

export type OrgBlueprintNodeType = 'company' | 'bu' | 'function' | 'department' | 'team' | 'project' | 'role' | 'squad';

export interface OrgUnit {
  id: string;
  name: string;
  type: OrgUnitType;
  parent_id?: string | null;
  parent_name?: string | null;
  owner_user_id?: string | null;
  owner_name?: string | null;
  description: string;
  node_type?: OrgBlueprintNodeType | string;
  hc_plan?: number;
  hc_actual?: number;
  risk_level?: 'low' | 'medium' | 'high' | string;
  status: 'active' | 'archived';
  sort_order?: number;
  member_count?: number;
  key_person_count?: number;
  assignment_count?: number;
  recent_changed_at?: string | null;
  ideal_composition?: string;
  is_shadow?: number;
  linked_position_id?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface OrgMember {
  id: string;
  org_unit_id: string;
  org_unit_name?: string;
  user_id: string;
  display_name?: string;
  username?: string;
  role?: User['role'];
  title_in_team: string;
  is_key_person: number;
  is_manager: number;
  start_date?: string;
  end_date?: string;
  status: 'active' | 'inactive';
  created_at?: string;
  updated_at?: string;
}

export interface OrgResponsibility {
  id: string;
  name: string;
  category: string;
  description: string;
  status: 'active' | 'archived';
  sort_order?: number;
  assignment_count?: number;
  created_at?: string;
  updated_at?: string;
}

export interface OrgAssignment {
  id: string;
  responsibility_id: string;
  responsibility_name?: string;
  responsibility_category?: string;
  org_unit_id: string;
  org_unit_name?: string;
  org_unit_type?: OrgUnitType;
  owner_user_id?: string | null;
  owner_name?: string | null;
  backup_user_id?: string | null;
  backup_name?: string | null;
  role_type: 'owner' | 'backup' | 'participant' | 'advisor';
  coverage_note: string;
  effective_from?: string;
  effective_to?: string;
  status: 'active' | 'inactive';
  created_at?: string;
  updated_at?: string;
}

export interface OrgChangeLog {
  id: string;
  change_type: string;
  entity_type: string;
  entity_id: string;
  summary: string;
  before_json?: string;
  after_json?: string;
  changed_by?: string | null;
  changed_by_name?: string | null;
  changed_at: string;
}

export interface OrgOverviewResponse {
  units: OrgUnit[];
  members: OrgMember[];
  responsibilities: OrgResponsibility[];
  assignments: OrgAssignment[];
  stats: {
    unit_count: number;
    key_person_count: number;
    recent_change_count: number;
    single_point_risk_count: number;
    vacant_responsibility_count: number;
  };
}

export interface OrgBlueprintResponse {
  nodes: OrgUnit[];
}

export interface NeedComment {
  id: string;
  need_id: string;
  user_id: string;
  author_name: string;
  content: string;
  mentioned_user_ids: string;
  created_at: string;
}

export interface NeedJdHistory {
  id: string;
  need_id: string;
  user_id: string;
  editor_name: string;
  field: string;
  old_value: string;
  new_value: string;
  created_at: string;
}

export interface HiringNeedDetail extends HiringNeed {
  members: NeedMember[];
  debates: NeedDebate[];
  comments: NeedComment[];
  jd_history: NeedJdHistory[];
}

// ====== 编制管理 (Headcount) ======
export type HcType = 'new' | 'replace' | 'temp';
/**
 * 编制五态（与 server/models/headcount.js 的 HC_STATUS 一一对应）：
 *   AVAILABLE  空闲，可被岗位绑定
 *   RECRUITING 招聘中，已被岗位绑定
 *   RESERVED   Offer 锁定，Offer 流转中
 *   OCCUPIED   已占用，候选人已入职
 *   CLOSED     已关闭
 */
export type HeadcountSlotStatus = 'AVAILABLE' | 'RECRUITING' | 'RESERVED' | 'OCCUPIED' | 'CLOSED';
export type HcStatus = HeadcountSlotStatus;
export type HcRequestStatus = 'pending' | 'approved' | 'rejected' | 'cancelled';

export const HC_TYPE_LABELS: Record<HcType, string> = {
  new: '新增编制', replace: '替换编制', temp: '临时编制',
};
export const HC_STATUS_LABELS: Record<HeadcountSlotStatus, string> = {
  AVAILABLE: '空闲', RECRUITING: '招聘中', RESERVED: 'Offer锁定', OCCUPIED: '已占用', CLOSED: '已关闭',
};
/** 旧小写状态 → 五态，用于兼容历史变更日志与旧本地缓存 */
export const HC_LEGACY_STATUS_MAP: Record<string, HeadcountSlotStatus> = {
  unused: 'AVAILABLE', recruiting: 'RECRUITING', onboarded: 'OCCUPIED', closed: 'CLOSED',
};
/** 归一化任意来源的编制状态值；无法识别时返回 null */
export function normalizeHcStatus(value?: string | null): HeadcountSlotStatus | null {
  if (!value) return null;
  const raw = String(value).trim();
  if (raw in HC_STATUS_LABELS) return raw as HeadcountSlotStatus;
  return HC_LEGACY_STATUS_MAP[raw.toLowerCase()] || null;
}
/**
 * 合法流转表（与 server/models/headcount.js 的 HC_TRANSITIONS 一致）。
 * 前端据此收敛可选项，非法跳转后端状态机同样会拒绝。
 */
export const HC_TRANSITIONS: Record<HeadcountSlotStatus, HeadcountSlotStatus[]> = {
  AVAILABLE: ['RECRUITING', 'CLOSED'],
  RECRUITING: ['RESERVED', 'AVAILABLE', 'CLOSED'],
  RESERVED: ['RECRUITING', 'OCCUPIED'],
  OCCUPIED: ['CLOSED'],
  CLOSED: ['AVAILABLE'],
};
export type HcDemandType = 'business' | 'backfill' | 'elimination';
export const HC_DEMAND_LABELS: Record<HcDemandType, string> = {
  business: '业务新增', backfill: '离职替补', elimination: '主动汰换',
};
export type HcEmploymentType = 'fulltime' | 'intern' | 'outsource' | 'consultant' | 'temp';
export const HC_EMPLOYMENT_LABELS: Record<HcEmploymentType, string> = {
  fulltime: '正式', intern: '实习', outsource: '外包', consultant: '顾问', temp: '临时工',
};
export const HC_REQUEST_STATUS_LABELS: Record<HcRequestStatus, string> = {
  pending: '待审批', approved: '已通过', rejected: '已驳回', cancelled: '已撤回',
};

export interface HeadcountSlot {
  id: string;
  code: string;
  position_name: string;
  department: string;
  sub_department?: string;
  org_unit_id?: string | null;
  job_level: string;
  hc_type: HcType;
  status: HeadcountSlotStatus;
  status_label?: string;
  version?: number;
  employment_type?: HcEmploymentType;
  owner_id?: string | null;
  owner_name?: string | null;
  budget_annual: number | null;
  actual_salary: number | null;
  position_id?: string | null;
  // 新链路：来自 position_headcount_bindings 的 active 绑定，用于跳转岗位详情
  bound_position_id?: string | null;
  bound_position_name?: string | null;
  bound_position_code?: string | null;
  candidate_id?: string | null;
  candidate_name?: string | null;
  offer_id?: string | null;
  reserved_at?: string | null;
  occupied_at?: string | null;
  request_id?: string | null;
  year: string;
  notes?: string;
  closed_at?: string | null;
  created_at: string;
  updated_at?: string;
}

/** 岗位↔编制绑定记录（GET /api/positions/:id/bindings） */
export interface PositionBinding {
  id: string;
  position_id: string;
  headcount_slot_id: string;
  status: 'active' | 'inactive';
  bound_at: string;
  bound_by?: string | null;
  bound_by_name?: string | null;
  unbound_at?: string | null;
  unbound_by?: string | null;
  unbind_reason?: string;
  slot: HeadcountSlot;
}

/** 暂停 / 关闭岗位前的编制释放预览（GET /api/positions/:id/release-preview） */
export interface ReleasedSlotInfo {
  code: string;
  status: HeadcountSlotStatus;
  status_label: string;
  reason?: string;
}
export interface ReleasePreview {
  will_release: ReleasedSlotInfo[];
  will_keep: ReleasedSlotInfo[];
}
/** 暂停 / 关闭岗位的返回结果 */
export interface PositionLifecycleResult {
  position: Position;
  released: ReleasedSlotInfo[];
  skipped: ReleasedSlotInfo[];
}

/** 批量关闭前的释放预览（POST /api/positions/batch-release-preview） */
export interface BatchReleasePreviewItem {
  id: string;
  name: string;
  code?: string | null;
  status: PositionStatus;
  status_label: string;
  will_release: ReleasedSlotInfo[];
  will_keep: ReleasedSlotInfo[];
}
export interface BatchReleasePreview {
  items: BatchReleasePreviewItem[];
  release_total: number;
  keep_total: number;
}
/**
 * 批量关闭结果（POST /api/positions/batch-close）。
 * closed[].kept 是因 Offer 流转 / 已入职而保留的编制，与岗位层的 skipped（本就已关闭）不是一回事。
 */
export interface BatchCloseResult {
  closed: { id: string; name: string; released: ReleasedSlotInfo[]; kept: ReleasedSlotInfo[] }[];
  skipped: { id: string; name: string; reason: string }[];
  failed: { id: string; name: string; error: string }[];
}

// ====== Offer（编制锁定链路，区别于存量 offer_approvals 审批流水） ======
export type OfferStatus =
  | 'pending_approval' | 'approved' | 'rejected' | 'sent'
  | 'accepted' | 'declined' | 'withdrawn' | 'completed';

export const OFFER_STATUS_LABELS: Record<OfferStatus, string> = {
  pending_approval: '待审批', approved: '已审批通过', rejected: '审批拒绝',
  sent: '已发送候选人', accepted: '候选人接受', declined: '候选人拒绝',
  withdrawn: '已撤回', completed: '已入职完成',
};
/** 进行中的 Offer 占用编制锁 */
export const OFFER_IN_PROGRESS_STATUSES: OfferStatus[] = [
  'pending_approval', 'approved', 'sent', 'accepted',
];
export type OfferAction = 'approve' | 'reject' | 'send' | 'accept' | 'decline' | 'withdraw' | 'complete';

export interface Offer {
  id: string;
  code?: string;
  candidate_id: string;
  candidate_name?: string;
  position_id: string;
  position_name?: string;
  position_code?: string;
  headcount_slot_id: string;
  slot_code?: string;
  slot_status?: HeadcountSlotStatus;
  slot_status_label?: string;
  status: OfferStatus;
  status_label?: string;
  in_progress?: boolean;
  salary_annual?: number;
  employment_type?: HcEmploymentType;
  expected_onboard_date?: string;
  probation_months?: number;
  work_location?: string;
  notes?: string;
  // 薪酬明细（Offer 录用通知书待填字段）
  comp_amount?: number;
  comp_unit?: string;
  comp_currency?: string;
  comp_pretax?: number;
  base_salary?: number;
  performance_salary?: number;
  intern_end_date?: string;
  accept_deadline?: string;
  perks?: string;
  // 社招（正式）专有薪酬字段
  contract_end_date?: string;
  probation_end_date?: string;
  probation_salary?: number;
  year_end_multiplier?: string;
  equity_value?: number;
  // 薪资拆细 + 其他说明 + 附件（北森模式）
  full_attendance_bonus?: number;
  post_allowance?: number;
  transport_allowance?: number;
  skill_allowance_pre?: number;
  skill_allowance_post?: number;
  management_allowance?: number;
  monthly_performance?: number;
  quarterly_performance?: number;
  half_year_performance?: number;
  sign_on_bonus?: number;
  has_noncompete?: number;
  other_notes?: string;
  assessment_file?: string;
  background_check_file?: string;
  noncompete_file?: string;
  // 统一 Offer 流程：审批 / 附件字段
  reason?: string;
  offer_image?: string;
  offer_pdf?: string;
  approver_user_ids?: string[];
  approver_id?: string | null;
  approver_name?: string | null;
  approver_comment?: string;
  approved_at?: string | null;
  // 节点式审批链
  approval_chain?: OfferApprovalNode[];
  approval_stage?: number;   // 当前活动节点下标；-1 表示无待办
  pending_for_me?: boolean;  // 当前登录用户是否为当前活动节点待审批人
  awaiting_attachment?: boolean;  // CEO+CFO 会签前是否卡在「待 HR 附上 Offer 附件」
  redacted?: 'no_salary' | 'attachment_only';  // 后端脱敏级别（若有）
  created_by?: string | null;
  created_by_name?: string | null;
  created_at: string;
  updated_at?: string;
}

// Offer 审批节点（业务VP / HRD / CEO+CFO）
export type OfferApprovalNodeStatus = 'pending' | 'approved' | 'rejected' | 'skipped';
export interface OfferApprovalApprover {
  user_id: string;
  name: string;
  role_label: string;
  status: OfferApprovalNodeStatus;
  comment?: string;
  at?: string;
  auto?: boolean;
  skip_reason?: string;
}
export interface OfferApprovalNode {
  key: 'business_vp' | 'hrd' | 'ceo_cfo';
  label: string;
  mode: 'notify' | 'gate';
  sign: 'single' | 'countersign';
  visibility: 'no_salary' | 'full' | 'attachment_only';
  approvers: OfferApprovalApprover[];
  status: OfferApprovalNodeStatus;
  decided_at?: string;
  auto?: boolean;
  skip_reason?: string;
}

export interface HeadcountRequest {
  id: string;
  department: string;
  sub_department?: string;
  position_name: string;
  job_level: string;
  hc_type: HcType;
  headcount: number;
  budget_annual: number | null;
  urgency: NeedUrgency;
  reason: string;
  status: HcRequestStatus;
  applicant_id?: string | null;
  applicant_name?: string | null;
  approver_id?: string | null;
  approver_name?: string | null;
  approver_comment?: string;
  approved_at?: string | null;
  year: string;
  created_at: string;
  demand_type?: HcDemandType;
  employment_type?: HcEmploymentType;
  mentor?: string;
  report_to?: string;
  work_location?: string;
  expected_date?: string;
  interviewer_first?: string;
  interviewer_second?: string;
  interviewer_final?: string;
  goal_90d?: string;
  talent_profile?: string;
  team_docs?: TeamDoc[];
  requester_id?: string | null;
  requester_name?: string | null;
  // 三级审批字段
  approver_l1_id?: string | null;
  approver_l1_name?: string | null;
  l1_status?: 'pending' | 'approved' | 'rejected';
  l1_comment?: string;
  l1_at?: string;
  approver_l2_id?: string | null;
  approver_l2_name?: string | null;
  l2_status?: 'pending' | 'approved' | 'rejected';
  l2_comment?: string;
  l2_at?: string;
  approver_l3_id?: string | null;
  approver_l3_name?: string | null;
  l3_status?: 'pending' | 'approved' | 'rejected';
  l3_comment?: string;
  l3_at?: string;
  current_level?: number; // 1/2/3 当前待审批级别，0=流程结束
  pending_for_me?: boolean; // 当前用户是否可审批
}

export interface TeamDoc {
  title: string;
  url: string;
}

export interface HeadcountChange {
  id: string;
  slot_id?: string | null;
  request_id?: string | null;
  action: string;
  detail: string;
  old_value?: string;
  new_value?: string;
  user_id?: string | null;
  user_name?: string | null;
  slot_code?: string | null;
  slot_position?: string | null;
  created_at: string;
}

export interface HeadcountOverview {
  summary: {
    // 五态口径：used = reserved + occupied（编制已被实际消耗）
    total: number; used: number; recruiting: number; closed: number;
    available: number; reserved: number; occupied: number;
    // onboarded / unused 为兼容字段，语义等同 occupied / available
    onboarded: number; unused: number;
    fillRate: number;
    cost: { budget: number; actual: number } | null;
  };
  byDepartment: { department: string; total: number; occupied: number; reserved: number; recruiting: number; available: number; budget?: number }[];
  byLevel: { key: string; label: string; count: number }[];
  byType: { key: string; label: string; count: number }[];
  byStatus: { key: string; label: string; count: number }[];
  trend: { period: string; total: number; used: number; recruiting: number }[];
  costRanking: { department: string; headcount: number; total: number; rank: number; perCapita?: number; budget?: number }[] | null;
}

// ====== 人才中枢 ======
export interface Employee {
  id: string;
  name: string;
  avatar_url: string;
  employee_type: 'full_time' | 'intern' | 'outsource' | 'consultant';
  title: string;
  level: string;
  org_unit_id: string | null;
  user_id: string | null;
  candidate_id: string | null;
  core_output: string;
  workload: 'high' | 'medium' | 'low';
  monthly_cost: number;
  hire_date: string;
  is_leader?: number;
  resign_date?: string;
  recent_work_content?: { period: string; feishu_link?: string; report_summary?: string }[];
  status: 'active' | 'resigned' | 'on_leave';
  tags?: EmployeeTag[];
  created_at?: string;
  updated_at?: string;
}

export interface EmployeeTag {
  id: string;
  name: string;
  category?: 'skill' | 'experience' | 'certification';
  color: string;
}

export interface OrgSnapshot {
  id: string;
  name: string;
  snapshot_json?: any;
  created_by_name?: string;
  created_at: string;
}

export interface TalentRisk {
  type: 'critical' | 'warning' | 'info';
  category: 'vacancy' | 'overload' | 'orphan';
  unit_id: string;
  unit_name: string;
  message: string;
}

export interface TalentTreeResponse {
  units: OrgUnit[];
  employees: Employee[];
  unitStats: Record<string, { employee_count: number; total_monthly_cost: number; high_workload_count: number }>;
}

export const EMPLOYEE_TYPE_LABELS: Record<string, string> = {
  full_time: '正式',
  intern: '实习',
  outsource: '外包',
  consultant: '顾问',
};

export const WORKLOAD_LABELS: Record<string, string> = {
  high: '高负荷',
  medium: '正常',
  low: '低负荷',
};
